import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { listPosts } from '@/features/blog/services/blogApi';
import type { PaginatedResponse, Post } from '@/shared/types/api';
import PostCard from '@/features/blog/components/PostCard';
import Pagination from '@/shared/components/Pagination';
import { PostCardSkeleton } from '@/shared/components/Skeleton';
import Breadcrumbs from '@/shared/components/Breadcrumbs';

export default function TagPage() {
  const { id, slug } = useParams<{ id: string; slug?: string }>();
  const tagId = Number(id);
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery<PaginatedResponse<Post>>({
    queryKey: ['posts', 'tag', tagId, page],
    queryFn: () => listPosts({ page, perPage: 10, tag: tagId }),
    enabled: !!tagId,
  });

  const displayName = slug || `tag-${id}`;

  return (
    <div>
      <Helmet>
        <title>#{displayName} — Vladyslav Chebotarov</title>
        <link rel="canonical" href={`https://codder.cc/tag/${id}/${slug || ''}`} />
        <meta property="og:title" content={`#${displayName} — Vladyslav Chebotarov`} />
        <meta property="og:url" content={`https://codder.cc/tag/${id}/${slug || ''}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <Breadcrumbs items={[{ label: 'Blog', to: '/blog' }, { label: `#${displayName}` }]} />

      <h2 className="text-base font-bold mb-4">
        Posts tagged: <span className="text-accent">#{displayName}</span>
      </h2>
      <hr />

      {isLoading && (
        <>
          <PostCardSkeleton />
          <PostCardSkeleton />
        </>
      )}

      {error && (
        <div className="text-center py-12">
          <p className="text-muted text-sm mb-4">Failed to load posts.</p>
          <button onClick={() => window.location.reload()} className="btn-terminal text-xs">
            Retry
          </button>
        </div>
      )}

      {data && data.posts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted text-sm">No posts found with this tag.</p>
        </div>
      )}

      {data?.posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.total_pages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
